// @ts-nocheck
/**
 * fileTableCsv — pure CSV encode/decode for fileTable parts (no filesystem I/O).
 *
 * Header row uses column ids. Data rows are positional. Part files after the first
 * omit the header when concatenated for logical export (first part keeps header).
 */

import type { TableColumnDefinition, TablePropertySchema } from '../../types/category';
import type { TableCellValue } from './tableCodec';
import { isTableColumnType, TABLE_CODEC_LIMITS } from './tableCodec';

export type FileTableCsvErrorCode =
  | 'invalid_schema'
  | 'invalid_csv'
  | 'header_mismatch'
  | 'column_count'
  | 'invalid_value'
  | 'value_too_long';

export type FileTableCsvOutcome<T> =
  | { ok: true; value: T }
  | { ok: false; code: FileTableCsvErrorCode; message: string };

function escapeCsvField(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields;
}

function splitCsvLines(csv: string): string[] {
  const normalized = csv.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < normalized.length; i += 1) {
    const ch = normalized[i]!;
    if (ch === '"') {
      inQuotes = !inQuotes;
      current += ch;
    } else if (ch === '\n' && !inQuotes) {
      if (current.length > 0 || lines.length === 0) {
        lines.push(current);
      }
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.length > 0 || lines.length === 0) {
    lines.push(current);
  }
  return lines.filter((line, index) => index === 0 || line.trim().length > 0);
}

/** Validate columns only — no maxRows / maxStringBytes caps for fileTable. */
export function validateFileTableSchema(schema: TablePropertySchema): string | null {
  if (!schema.columns.length) {
    return 'Table schema requires at least one column.';
  }
  if (schema.columns.length > TABLE_CODEC_LIMITS.maxColumns) {
    return `Table schema exceeds max columns (${TABLE_CODEC_LIMITS.maxColumns}).`;
  }
  const ids = new Set<string>();
  for (const col of schema.columns) {
    if (!col.id) {
      return 'Each table column requires a non-empty id.';
    }
    if (ids.has(col.id)) {
      return `Duplicate table column id: ${col.id}`;
    }
    ids.add(col.id);
    if (!isTableColumnType(col.type)) {
      return `Column '${col.id}' has unsupported type '${String(col.type)}'.`;
    }
  }
  return null;
}

/**
 * Strip CTBL-only fields from a fileTable schema (authoring ballast cleanup).
 * Returns columns-only schema suitable for persisted fileTable definitions.
 */
export function stripFileTableSchemaBallast(schema: TablePropertySchema): TablePropertySchema {
  return {
    columns: schema.columns.map((col) => {
      const next: TableColumnDefinition = {
        id: col.id,
        headerName: col.headerName,
        type: col.type,
      };
      if (col.editable === false) {
        next.editable = false;
      }
      return next;
    }),
  };
}

export function fileTableHeaderLine(schema: TablePropertySchema): string {
  return schema.columns.map((col) => escapeCsvField(col.id)).join(',');
}

export function formatFileTableCell(value: TableCellValue): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }
  return String(value);
}

export function encodeFileTableRow(values: readonly TableCellValue[]): string {
  return values.map((v) => escapeCsvField(formatFileTableCell(v))).join(',');
}

export function buildHeaderOnlyCsv(schema: TablePropertySchema): string {
  return `${fileTableHeaderLine(schema)}\n`;
}

function coerceCell(
  raw: string,
  col: TableColumnDefinition,
  enforceStringLimit: boolean,
): FileTableCsvOutcome<TableCellValue> {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return { ok: true, value: null };
  }
  switch (col.type) {
    case 'int': {
      if (!/^-?\d+$/.test(trimmed)) {
        return { ok: false, code: 'invalid_value', message: `Expected integer for column ${col.id}` };
      }
      return { ok: true, value: Number.parseInt(trimmed, 10) };
    }
    case 'int64': {
      if (!/^-?\d+$/.test(trimmed)) {
        return { ok: false, code: 'invalid_value', message: `Expected integer for column ${col.id}` };
      }
      const n = Number(trimmed);
      if (!Number.isSafeInteger(n)) {
        return {
          ok: false,
          code: 'invalid_value',
          message: `Expected safe integer for column ${col.id}`,
        };
      }
      return { ok: true, value: n };
    }
    case 'double': {
      const n = Number(trimmed);
      if (!Number.isFinite(n)) {
        return { ok: false, code: 'invalid_value', message: `Expected number for column ${col.id}` };
      }
      return { ok: true, value: n };
    }
    case 'timestamp': {
      if (/^-?\d+$/.test(trimmed)) {
        return { ok: true, value: Number.parseInt(trimmed, 10) };
      }
      const ms = Date.parse(trimmed);
      if (!Number.isFinite(ms)) {
        return { ok: false, code: 'invalid_value', message: `Invalid timestamp for column ${col.id}` };
      }
      return { ok: true, value: ms };
    }
    case 'color': {
      if (!/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(trimmed)) {
        return {
          ok: false,
          code: 'invalid_value',
          message: `Expected hex color for column ${col.id}`,
        };
      }
      return { ok: true, value: trimmed };
    }
    case 'string':
    default: {
      if (enforceStringLimit) {
        const maxBytes = col.maxStringBytes ?? TABLE_CODEC_LIMITS.maxStringBytes;
        const byteLen = new TextEncoder().encode(trimmed).length;
        if (byteLen > maxBytes) {
          return {
            ok: false,
            code: 'value_too_long',
            message: `Text exceeds max ${maxBytes} bytes for column ${col.id}`,
          };
        }
      }
      return { ok: true, value: trimmed };
    }
  }
}

export function parseFileTableCsvPart(
  csv: string,
  schema: TablePropertySchema,
  options?: { hasHeader?: boolean; enforceStringLimit?: boolean },
): FileTableCsvOutcome<TableCellValue[][]> {
  const schemaError = validateFileTableSchema(schema);
  if (schemaError) {
    return { ok: false, code: 'invalid_schema', message: schemaError };
  }

  const hasHeader = options?.hasHeader !== false;
  const enforceStringLimit = options?.enforceStringLimit === true;
  const lines = splitCsvLines(csv.trim());
  if (lines.length === 0) {
    return { ok: true, value: [] };
  }

  let dataStart = 0;
  if (hasHeader) {
    const headers = parseCsvLine(lines[0]!).map((h) => h.trim());
    if (headers.length !== schema.columns.length) {
      return {
        ok: false,
        code: 'column_count',
        message: `Expected ${schema.columns.length} columns, got ${headers.length}`,
      };
    }
    for (let i = 0; i < schema.columns.length; i += 1) {
      if (headers[i] !== schema.columns[i]!.id) {
        return {
          ok: false,
          code: 'header_mismatch',
          message: `Header mismatch at column ${i}: expected "${schema.columns[i]!.id}", got "${headers[i]}"`,
        };
      }
    }
    dataStart = 1;
  }

  const rows: TableCellValue[][] = [];
  for (let lineIndex = dataStart; lineIndex < lines.length; lineIndex += 1) {
    const fields = parseCsvLine(lines[lineIndex]!);
    if (fields.every((f) => f.trim() === '')) {
      continue;
    }
    if (fields.length !== schema.columns.length) {
      return {
        ok: false,
        code: 'column_count',
        message: `Row ${lineIndex} has ${fields.length} columns; expected ${schema.columns.length}`,
      };
    }
    const row: TableCellValue[] = [];
    for (let c = 0; c < schema.columns.length; c += 1) {
      const coerced = coerceCell(fields[c] ?? '', schema.columns[c]!, enforceStringLimit);
      if (!coerced.ok) {
        return coerced;
      }
      row.push(coerced.value);
    }
    rows.push(row);
  }
  return { ok: true, value: rows };
}

/** Parse a full logical CSV export (single file with header) into rows. */
export function parseLogicalFileTableCsv(
  csv: string,
  schema: TablePropertySchema,
): FileTableCsvOutcome<TableCellValue[][]> {
  return parseFileTableCsvPart(csv, schema, { hasHeader: true, enforceStringLimit: false });
}

/** Build a single logical CSV (header + all rows) for Edit Entry export. */
export function encodeLogicalFileTableCsv(
  schema: TablePropertySchema,
  rows: readonly (readonly TableCellValue[])[],
): string {
  const lines = [fileTableHeaderLine(schema)];
  for (const row of rows) {
    lines.push(encodeFileTableRow(row));
  }
  return `${lines.join('\n')}\n`;
}

/**
 * Split rows into CSV part strings under maxPartBytes.
 * Part 0 includes header; subsequent parts are data-only (no header).
 */
export function splitRowsIntoCsvParts(
  schema: TablePropertySchema,
  rows: readonly (readonly TableCellValue[])[],
  maxPartBytes: number,
): string[] {
  const header = `${fileTableHeaderLine(schema)}\n`;
  const headerBytes = new TextEncoder().encode(header).length;
  const limit = Math.max(headerBytes + 1, maxPartBytes);

  if (rows.length === 0) {
    return [header];
  }

  const parts: string[] = [];
  let current = header;
  let currentBytes = headerBytes;
  let isFirstPart = true;

  for (const row of rows) {
    const line = `${encodeFileTableRow(row)}\n`;
    const lineBytes = new TextEncoder().encode(line).length;
    if (!isFirstPart && currentBytes + lineBytes > limit && currentBytes > 0) {
      parts.push(current);
      current = line;
      currentBytes = lineBytes;
      isFirstPart = false;
      continue;
    }
    if (isFirstPart && currentBytes + lineBytes > limit && current !== header) {
      parts.push(current);
      current = line;
      currentBytes = lineBytes;
      isFirstPart = false;
      continue;
    }
    current += line;
    currentBytes += lineBytes;
    isFirstPart = false;
  }
  parts.push(current);
  return parts;
}

/** Concatenate part CSVs into one logical CSV (header once). */
export function concatenateFileTableParts(
  schema: TablePropertySchema,
  partCsvs: readonly string[],
): FileTableCsvOutcome<string> {
  const allRows: TableCellValue[][] = [];
  for (let i = 0; i < partCsvs.length; i += 1) {
    const parsed = parseFileTableCsvPart(partCsvs[i]!, schema, {
      hasHeader: i === 0,
      enforceStringLimit: false,
    });
    if (!parsed.ok) {
      return parsed;
    }
    allRows.push(...parsed.value);
  }
  return { ok: true, value: encodeLogicalFileTableCsv(schema, allRows) };
}

export function appendRowToNewestPartCsv(
  newestPartCsv: string,
  schema: TablePropertySchema,
  cells: readonly TableCellValue[],
  isFirstPart: boolean,
): FileTableCsvOutcome<string> {
  const parsed = parseFileTableCsvPart(newestPartCsv, schema, {
    hasHeader: isFirstPart,
    enforceStringLimit: false,
  });
  if (!parsed.ok) {
    return parsed;
  }
  if (cells.length !== schema.columns.length) {
    return {
      ok: false,
      code: 'column_count',
      message: `Expected ${schema.columns.length} values, got ${cells.length}`,
    };
  }
  const rows = [...parsed.value, [...cells]];
  if (isFirstPart) {
    return { ok: true, value: encodeLogicalFileTableCsv(schema, rows) };
  }
  const lines = rows.map((row) => encodeFileTableRow(row));
  return { ok: true, value: `${lines.join('\n')}\n` };
}

export function utf8ByteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}
